import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import type { Feature } from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';
import { CATEGORIES, CATEGORY_META, type Category, type ListItem } from '@travel-planner/shared';
import { useUpdateItem } from '../lists/useListItems';
import { useMapContext } from './MapContext';
import { NewPinForm, type PendingPin } from './NewPinForm';
import { MarkerPanel } from './MarkerPanel';

function markerElement(item: ListItem, selected: boolean): HTMLDivElement {
  const el = document.createElement('div');
  const size = selected ? 30 : 22;
  el.style.cssText = `width:${size}px;height:${size}px;border-radius:50%;background:${
    CATEGORY_META[item.category].color
  };border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;font-size:${
    selected ? 15 : 12
  }px;cursor:pointer;`;
  el.textContent = CATEGORY_META[item.category].icon;
  el.title = item.title;
  return el;
}

export function MapView({
  tripId,
  countryId,
  countryName,
}: {
  tripId: string;
  countryId: string | null;
  countryName: string | null;
}) {
  const {
    placedItems,
    focusId,
    clearFocus,
    placingItemId,
    cancelPlacing,
    route,
    routeLabel,
    clearRoute,
  } = useMapContext();
  const placeUpdate = useUpdateItem(tripId, '');

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const addModeRef = useRef(false);
  const placingRef = useRef<string | null>(null);
  const fittedRef = useRef(false);
  const [ready, setReady] = useState(false);

  const [hidden, setHidden] = useState<Set<Category>>(new Set());
  const [onlyThisCountry, setOnlyThisCountry] = useState(true);
  const [addMode, setAddMode] = useState(false);
  const [pending, setPending] = useState<PendingPin | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(
    () =>
      placedItems.filter(
        (p) =>
          !hidden.has(p.category) && (!onlyThisCountry || !countryId || p.countryId === countryId),
      ),
    [placedItems, hidden, onlyThisCountry, countryId],
  );

  // Init the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: '/api/map/style',
      center: [10, 30],
      zoom: 1.4,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => setReady(true));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    addModeRef.current = addMode;
    placingRef.current = placingItemId;
    const map = mapRef.current;
    if (map) map.getCanvas().style.cursor = addMode || placingItemId ? 'crosshair' : '';
  }, [addMode, placingItemId]);

  // Map click → drop a new pin, or place the item awaiting placement.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const handler = (e: maplibregl.MapMouseEvent) => {
      const placing = placingRef.current;
      if (placing) {
        placeUpdate.mutate({ itemId: placing, patch: { lat: e.lngLat.lat, lng: e.lngLat.lng } });
        cancelPlacing();
        return;
      }
      if (!addModeRef.current) return;
      setPending({ lng: e.lngLat.lng, lat: e.lngLat.lat });
      setAddMode(false);
      setSelectedId(null);
    };
    map.on('click', handler);
    return () => {
      map.off('click', handler);
    };
  }, [ready, placeUpdate, cancelPlacing]);

  // Render markers whenever the visible set or selection changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = filtered.map((item) => {
      const el = markerElement(item, item.id === selectedId);
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        setSelectedId(item.id);
        setPending(null);
      });
      return new maplibregl.Marker({ element: el }).setLngLat([item.lng!, item.lat!]).addTo(map);
    });
  }, [filtered, ready, selectedId]);

  // Fit to markers once on first load with data.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || fittedRef.current || filtered.length === 0) return;
    const bounds = new maplibregl.LngLatBounds();
    filtered.forEach((p) => bounds.extend([p.lng!, p.lat!]));
    map.fitBounds(bounds, { padding: 60, maxZoom: 9, duration: 0 });
    fittedRef.current = true;
  }, [ready, filtered]);

  // Focus (pan) when a marker is requested from elsewhere (e.g. a list item).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !focusId) return;
    const item = placedItems.find((p) => p.id === focusId);
    if (item && item.lat !== null && item.lng !== null) {
      map.flyTo({ center: [item.lng, item.lat], zoom: Math.max(map.getZoom(), 10) });
      setSelectedId(item.id);
    }
    clearFocus();
  }, [focusId, ready, placedItems, clearFocus]);

  // Day-route overlay: draw the selected day's ordered stops as a line.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const SRC = 'day-route';
    const existing = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;

    if (!route || route.length < 1) {
      if (map.getLayer(`${SRC}-line`)) map.removeLayer(`${SRC}-line`);
      if (existing) map.removeSource(SRC);
      return;
    }

    const data: Feature = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: route.map((p) => [p.lng, p.lat]) },
    };
    if (existing) {
      existing.setData(data);
    } else {
      map.addSource(SRC, { type: 'geojson', data });
      map.addLayer({
        id: `${SRC}-line`,
        type: 'line',
        source: SRC,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#38bdf8', 'line-width': 3, 'line-dasharray': [2, 1] },
      });
    }

    const bounds = new maplibregl.LngLatBounds();
    route.forEach((p) => bounds.extend([p.lng, p.lat]));
    const first = route[0];
    if (route.length > 1) map.fitBounds(bounds, { padding: 80, maxZoom: 12 });
    else if (first) map.flyTo({ center: [first.lng, first.lat], zoom: 11 });
  }, [route, ready]);

  const selectedItem = selectedId ? placedItems.find((p) => p.id === selectedId) ?? null : null;

  return (
    <div style={{ position: 'relative', marginTop: '1rem' }}>
      <div
        ref={containerRef}
        className="tp-map"
        style={{ height: 460, borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)' }}
      />

      {/* Legend / category filter */}
      <div
        className="glass d-flex flex-wrap rounded-3 p-2"
        style={{ position: 'absolute', top: 10, left: 10, maxWidth: 260, gap: 4 }}
      >
        {CATEGORIES.map((c) => {
          const on = !hidden.has(c);
          return (
            <button
              key={c}
              onClick={() =>
                setHidden((prev) => {
                  const next = new Set(prev);
                  if (next.has(c)) next.delete(c);
                  else next.add(c);
                  return next;
                })
              }
              title={on ? 'Hide' : 'Show'}
              className="d-inline-flex align-items-center gap-1 border-0 bg-transparent text-body"
              style={{ fontSize: '0.72rem', cursor: 'pointer', opacity: on ? 1 : 0.3 }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: CATEGORY_META[c].color,
                  display: 'inline-block',
                }}
              />
              {CATEGORY_META[c].label}
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div style={{ position: 'absolute', bottom: 10, left: 10 }} className="d-flex gap-2 align-items-center">
        <button
          className={`btn btn-sm ${addMode ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => {
            setAddMode((v) => !v);
            setSelectedId(null);
            cancelPlacing();
          }}
          disabled={!countryId}
          title={!countryId ? 'Select a country first' : 'Add a located item'}
        >
          {addMode ? 'Click map to place…' : '+ Drop marker'}
        </button>
        {countryName && (
          <label className="glass d-flex gap-2 align-items-center rounded-3 px-2 py-1 m-0" style={{ fontSize: '0.8rem' }}>
            <input
              type="checkbox"
              className="form-check-input m-0"
              checked={onlyThisCountry}
              onChange={(e) => setOnlyThisCountry(e.target.checked)}
            />
            Only {countryName}
          </label>
        )}
      </div>

      {/* Placement banner */}
      {placingItemId && (
        <div className="glass" style={bannerStyle}>
          Click the map to set this item’s location
          <button onClick={cancelPlacing} className="btn btn-ghost btn-sm p-0 text-body" style={{ minHeight: 'auto' }}>
            ✕
          </button>
        </div>
      )}

      {/* Day-route banner */}
      {routeLabel && (
        <div className="glass" style={bannerStyle}>
          Route: {routeLabel}
          <button onClick={clearRoute} className="btn btn-ghost btn-sm p-0 text-body" style={{ minHeight: 'auto' }}>
            ✕
          </button>
        </div>
      )}

      {/* Side panels */}
      {(selectedItem || pending) && (
        <div style={{ position: 'absolute', top: 10, right: 56 }}>
          {pending && countryId ? (
            <NewPinForm tripId={tripId} countryId={countryId} pin={pending} onClose={() => setPending(null)} />
          ) : selectedItem ? (
            <MarkerPanel tripId={tripId} item={selectedItem} onClose={() => setSelectedId(null)} />
          ) : null}
        </div>
      )}
    </div>
  );
}

const bannerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 10,
  left: '50%',
  transform: 'translateX(-50%)',
  borderRadius: 999,
  padding: '0.35rem 0.9rem',
  fontSize: '0.8rem',
  display: 'flex',
  gap: 10,
  alignItems: 'center',
};
